/**
 * useAdsByOutlet — per-outlet × provider ads breakdown.
 *
 * Returns one row per outlet with swiggy + zomato spend / orders / revenue
 * (revenue derived from per-outlet AOV × ads orders, since fooddb stores
 * ROAS as a ratio not a revenue-side number).
 *
 * True period ROAS is computed as `revenue / spend` at the period level —
 * NOT as an average of daily ratios, which is mathematically wrong and
 * explodes on low-spend days (the source of the 495× bug).
 */
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '@/hooks/useDashboardContext';
import {
  fooddb,
  OUTLET_TO_FOODDB,
  FOODDB_TO_OUTLET,
  type AggregatorProvider,
} from '@/lib/supabase/fooddb';
import { slotsFor, type MetricConcept } from '@/lib/aggregator-metrics';
import { effectivePeriodRange, toISTDateString, type PeriodKey } from '@/lib/periods';

interface Args {
  period: PeriodKey;
  outletIds?: string[]; // marketing outlet ids; default = all mapped
  providers?: AggregatorProvider[];
}

export interface AdsPerProvider {
  spend: number;
  orders: number;
  netSales: number;
  delivered: number;
  /** Period AOV for this outlet × provider — used to derive ads revenue. */
  aov: number;
  /** True ROAS = (orders × aov) / spend. 0 if spend = 0. */
  roas: number;
}

export interface AdsOutletRow {
  outletId: string;
  swiggy: AdsPerProvider;
  zomato: AdsPerProvider;
  totalSpend: number;
  totalOrders: number;
  totalAdRevenue: number;
  combinedRoas: number;
  /** 'swiggy' | 'zomato' | null — which platform has higher ROAS. */
  bestPlatform: AggregatorProvider | null;
}

export interface AdsByOutletResult {
  rows: AdsOutletRow[];
  totals: {
    spend: number;
    orders: number;
    revenue: number;
    roas: number;
  };
}

const CONCEPTS: MetricConcept[] = [
  'ads.spend',
  'ads.orders',
  'sales.net_sales',
  'sales.delivered_orders',
];

export function useAdsByOutlet({ period, outletIds, providers }: Args) {
  const { customRange } = useDashboard();
  const range = effectivePeriodRange(period, customRange);
  const selectedProviders: AggregatorProvider[] = providers ?? ['swiggy', 'zomato'];
  return useQuery<AdsByOutletResult>({
    queryKey: [
      'ads-by-outlet',
      range.start.toISOString(),
      outletIds?.join(',') ?? 'all',
      selectedProviders.join(','),
    ],
    queryFn: async () => {
      const targetOutlets = outletIds ?? Object.keys(OUTLET_TO_FOODDB);
      const slugs = targetOutlets
        .map((id) => OUTLET_TO_FOODDB[id])
        .filter(Boolean);

      // Build a flat list of (provider, category, metric) tuples to query.
      const tuples: { provider: AggregatorProvider; category: string; metric: string; concept: MetricConcept }[] = [];
      for (const provider of selectedProviders) {
        for (const concept of CONCEPTS) {
          for (const slot of slotsFor(provider, concept)) {
            tuples.push({ provider, category: slot.category, metric: slot.metric, concept });
          }
        }
      }

      const start = toISTDateString(range.start);
      const end = toISTDateString(new Date(range.end.getTime() - 1));

      // One query per (provider, category, metric) tuple, filtered to our
      // outlet slugs and date range. Parallel fetches; sum per (outlet, concept).
      const fetches = tuples.map((t) =>
        (async () => {
          const { data, error } = await fooddb
            .from('flax_metrics_daily')
            .select('outlet_id, value')
            .eq('provider', t.provider)
            .eq('category', t.category)
            .eq('metric', t.metric)
            .gte('date', start)
            .lte('date', end)
            .in('outlet_id', slugs);
          if (error) throw error;
          return { tuple: t, rows: (data ?? []) as Array<{ outlet_id: string; value: number | string }> };
        })(),
      );
      const results = await Promise.all(fetches);

      // Accumulate per outlet × provider × concept
      type Acc = Partial<Record<MetricConcept, number>>;
      const empty = (): Acc => ({});

      const acc: Record<string, { swiggy: Acc; zomato: Acc }> = {};
      for (const { tuple, rows } of results) {
        for (const r of rows) {
          const outletId = FOODDB_TO_OUTLET[r.outlet_id];
          if (!outletId) continue;
          if (!acc[outletId]) acc[outletId] = { swiggy: empty(), zomato: empty() };
          const bucket = acc[outletId][tuple.provider];
          bucket[tuple.concept] = (bucket[tuple.concept] ?? 0) + Number(r.value ?? 0);
        }
      }

      const rows: AdsOutletRow[] = targetOutlets.map((outletId) => {
        const a = acc[outletId] ?? { swiggy: empty(), zomato: empty() };
        const sw = derive(a.swiggy);
        const zo = derive(a.zomato);
        const totalSpend = sw.spend + zo.spend;
        const totalOrders = sw.orders + zo.orders;
        const totalAdRevenue = sw.orders * sw.aov + zo.orders * zo.aov;
        const combinedRoas = totalSpend > 0 ? totalAdRevenue / totalSpend : 0;
        const bestPlatform =
          sw.spend === 0 && zo.spend === 0
            ? null
            : sw.roas > zo.roas
              ? 'swiggy'
              : 'zomato';
        return {
          outletId,
          swiggy: sw,
          zomato: zo,
          totalSpend,
          totalOrders,
          totalAdRevenue,
          combinedRoas,
          bestPlatform,
        };
      });

      const totals = rows.reduce(
        (t, r) => ({
          spend: t.spend + r.totalSpend,
          orders: t.orders + r.totalOrders,
          revenue: t.revenue + r.totalAdRevenue,
          roas: 0,
        }),
        { spend: 0, orders: 0, revenue: 0, roas: 0 },
      );
      totals.roas = totals.spend > 0 ? totals.revenue / totals.spend : 0;

      return { rows, totals };
    },
    staleTime: 60_000,
  });
}

function derive(a: Partial<Record<MetricConcept, number>>): AdsPerProvider {
  const spend = a['ads.spend'] ?? 0;
  const orders = a['ads.orders'] ?? 0;
  const netSales = a['sales.net_sales'] ?? 0;
  const delivered = a['sales.delivered_orders'] ?? 0;
  const aov = delivered > 0 ? netSales / delivered : 0;
  const roas = spend > 0 ? (orders * aov) / spend : 0;
  return { spend, orders, netSales, delivered, aov, roas };
}
